import { useCallback, useEffect, useState } from 'react';
import { PlanExpert } from './plan';
import {
  api, ErreurApi,
  type DroitAffiche, type FicheTitulaire, type ResultatInscription,
  type SituationFonciere, type TitulaireResume,
} from './api';

/**
 * FONCIER (§9) — la situation juridique d'un fonds, et qui le détient.
 *
 * ⚠️ CET ÉCRAN N'INSCRIT AUCUN DROIT, ET C'EST VOULU. Un droit naît d'une
 * MUTATION validée à quatre yeux (rubrique 📝 Mutations). Poser ici un bouton
 * « ajouter un titulaire » donnerait un second chemin d'écriture — sans double
 * validation, sans radiation du droit précédent, sans journal de mutation.
 * Ce qui s'écrit ici est ce qui NE TRANSFÈRE PAS la propriété : les servitudes
 * et restrictions, qui grèvent un fonds sans en changer le titulaire.
 *
 * ⚠️ ET L'ÉCRAN DIT CE QU'IL NE VOIT PAS. `acces.nominatif` distingue « cette
 * parcelle n'a pas de titulaire » de « votre rôle ne vous montre pas les noms ».
 * Les deux produisent une liste sans nom ; ils n'appellent pas la même action.
 */

const ETIQUETTE_NATURE: Record<string, string> = {
  propriete: 'Propriété', usufruit: 'Usufruit', nue_propriete: 'Nue-propriété',
  bail_emphyteotique: 'Bail emphytéotique', occupation_coutumiere: 'Occupation coutumière',
  concession: 'Concession', servitude: 'Servitude', hypotheque: 'Hypothèque',
  saisie: 'Saisie', prenotation: 'Prénotation',
};

const TYPES_RESTRICTION = [
  'servitude_passage', 'servitude_vue', 'zone_non_aedificandi',
  'domaine_public', 'protection', 'alignement', 'reculement',
] as const;

type Onglet = 'parcelle' | 'titulaires' | 'plan';

export function Foncier(
  { parcelleInitiale, titulaireInitial, peutGreverr, peutInscrire, onOuvrirParcelle }:
  {
    parcelleInitiale?: string | null;
    /**
     * ⚠️⚠️ LA FICHE D'UNE PERSONNE EXISTAIT ET N'ÉTAIT ATTEIGNABLE QUE D'ICI.
     * La recherche universelle TROUVE les personnes — `recherche.ts:70` rend
     * `titulaire.id` — et les rendait en `<span>` inerte : on voyait le nom, on
     * ne pouvait pas l'ouvrir. Pendant ce temps, l'onglet « titulaires » de cet
     * écran chargeait déjà `api.foncier.titulaire(id)` et affichait le
     * patrimoine. Il ne manquait pas d'écran : il manquait un chemin.
     */
    titulaireInitial?: string | null;
    peutGreverr: boolean; peutInscrire: boolean;
    onOuvrirParcelle: (id: string) => void;
  },
) {
  const [onglet, setOnglet] = useState<Onglet>(titulaireInitial ? 'titulaires' : 'parcelle');
  const [saisie, setSaisie] = useState(parcelleInitiale ?? '');
  const [identifiant, setIdentifiant] = useState<string | null>(parcelleInitiale ?? null);
  const [situation, setSituation] = useState<SituationFonciere | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);
  const [grever, setGrever] = useState(false);

  const charger = useCallback(async () => {
    if (!identifiant) { setSituation(null); return; }
    setChargement(true); setErreur(null);
    try {
      setSituation(await api.foncier.parcelle(identifiant));
    } catch (e) {
      setSituation(null);
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter la situation foncière (fon.droit:lire)."
        : String((e as Error).message ?? e));
    } finally { setChargement(false); }
  }, [identifiant]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Foncier</h2>
        <nav className="onglets">
          <button className={onglet === 'parcelle' ? 'actif' : ''} onClick={() => setOnglet('parcelle')}>
            Situation d'une parcelle
          </button>
          <button className={onglet === 'titulaires' ? 'actif' : ''} onClick={() => setOnglet('titulaires')}>
            Titulaires
          </button>
          <button className={onglet === 'plan' ? 'actif' : ''} onClick={() => setOnglet('plan')}
                  disabled={!identifiant} title={identifiant ? 'Plan expert (§7)' : "Ouvrez d'abord une parcelle"}>
            Plan expert
          </button>
        </nav>
      </header>

      {message && <p className="succes" role="status">{message}</p>}

      {onglet === 'plan' && identifiant
        ? <PlanExpert identifiant={identifiant} />
        : onglet === 'titulaires'
        ? <RechercheTitulaires onOuvrirParcelle={onOuvrirParcelle} peutInscrire={peutInscrire}
                               titulaireInitial={titulaireInitial} />
        : (
          <>
            <form
              className="recherche large"
              onSubmit={(e) => { e.preventDefault(); setIdentifiant(saisie.trim() || null); }}
            >
              <input
                value={saisie} onChange={(e) => setSaisie(e.target.value)}
                placeholder="Identifiant de parcelle — ex. GN-PAR-000000001"
                aria-label="Identifiant de parcelle"
              />
              <button className="bouton primaire">Ouvrir la situation</button>
            </form>

            {erreur && <p className="erreur" role="alert">{erreur}</p>}
            {chargement && <p className="vide">Lecture de la situation juridique…</p>}
            {!chargement && !situation && !erreur && (
              <p className="vide">
                Saisissez un identifiant de parcelle. La situation juridique dit qui détient
                le fonds, dans quelles proportions, et ce qui le grève.
              </p>
            )}

            {situation && (
              <SituationParcelle
                s={situation}
                peutGreverr={peutGreverr}
                onGrever={() => setGrever(true)}
                onLever={async (id) => {
                  const motif = window.prompt('Lever cette restriction — motif consigné au journal :');
                  if (!motif || motif.trim().length < 3) return;
                  try {
                    const r = await api.foncier.leverRestriction(id, motif.trim());
                    setMessage(r.message); await charger();
                  } catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); }
                }}
              />
            )}

            {grever && situation && (
              <FormulaireRestriction
                parcelle={situation.parcelle.identifiant}
                onFerme={() => setGrever(false)}
                onInscrite={(m) => { setGrever(false); setMessage(m); void charger(); }}
              />
            )}
          </>
        )}
    </div>
  );
}

function SituationParcelle(
  { s, peutGreverr, onGrever, onLever }:
  { s: SituationFonciere; peutGreverr: boolean; onGrever: () => void; onLever: (id: string) => void },
) {
  const i = s.indivision;
  const restrictionsActives = s.restrictions.filter((r) => r.valide_au === null);

  return (
    <>
      <div className="entete-fiche">
        <h3><code>{s.parcelle.identifiant}</code></h3>
        <span className={`etat ${s.parcelle.statut === 'active' ? 'vert' : 'ambre'}`}>{s.parcelle.statut}</span>
        <span className="aide">{Number(s.parcelle.surface_m2).toLocaleString('fr-FR')} m² · origine {s.parcelle.origine}</span>
      </div>

      {/* ⚠️ L'ÉTAT DE L'INDIVISION EN PREMIER, ET EN TOUTES LETTRES. C'est la
          question qu'on vient poser : le fonds est-il entièrement attribué ?
          Un pourcentage l'aurait dit approximativement — 1/3 devient 33,3 %,
          et trois tiers cessent de faire le tout. */}
      <div className={i.excedentaire ? 'avertissement danger' : 'avertissement'}>
        <strong>
          {i.parts === 0 ? 'Non immatriculée'
            : i.excedentaire ? `Parts excédentaires — ${i.num}/${i.den}`
            : i.complet ? 'Propriété entièrement attribuée'
            : `Attribution partielle — ${i.num}/${i.den}`}
        </strong>
        <p style={{ margin: '6px 0 0' }}>{i.lecture}</p>
      </div>

      <h3 className="titre-section">
        Droits actifs <span className="aide">({s.droits.actifs.length})</span>
      </h3>
      {!s.acces.nominatif && (
        <p className="note-indispo">
          Votre rôle ouvre la structure des droits, pas l'identité des titulaires
          (sensibilité 2 requise). Ce n'est pas un registre incomplet — ce sont des
          noms que vous n'êtes pas habilité à lire.
        </p>
      )}
      {s.droits.actifs.length === 0
        ? <p className="vide">Aucun droit actif. Cette parcelle n'a pas encore été immatriculée.</p>
        : <TableDroits droits={s.droits.actifs} />}

      <h3 className="titre-section">
        Restrictions et servitudes <span className="aide">({restrictionsActives.length} active(s))</span>
        {peutGreverr && (
          <button className="bouton discret" style={{ marginLeft: 12 }} onClick={onGrever}>Grever ce fonds</button>
        )}
      </h3>
      {!s.acces.restrictions_lisibles && (
        <p className="note-indispo">Les restrictions relèvent de l'urbanisme — votre rôle ne les ouvre pas.</p>
      )}
      {s.restrictions.length === 0
        ? <p className="vide">Aucune restriction inscrite.</p>
        : (
          <table className="tableau large">
            <thead><tr><th>Type</th><th>Description</th><th>Bénéficiaire</th><th>Depuis</th><th>État</th><th /></tr></thead>
            <tbody>
              {s.restrictions.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.type.replace(/_/g, ' ')}</strong>
                      {r.emprise_partielle && <><br /><span className="aide">emprise partielle</span></>}</td>
                  <td>{r.description}</td>
                  <td>{r.beneficiaire_parcelle ? <code>{r.beneficiaire_parcelle}</code> : '—'}</td>
                  <td>{new Date(r.valide_du).toLocaleDateString('fr-FR')}</td>
                  <td>{r.valide_au
                    ? <span className="etat gris">levée le {new Date(r.valide_au).toLocaleDateString('fr-FR')}</span>
                    : <span className="etat vert">en vigueur</span>}</td>
                  <td className="actions-ligne">
                    {peutGreverr && r.valide_au === null && (
                      <button className="bouton discret danger" onClick={() => onLever(r.id)}>Lever</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {s.titres.length > 0 && (
        <>
          <h3 className="titre-section">Titres et actes <span className="aide">({s.titres.length})</span></h3>
          <table className="tableau large">
            <thead><tr><th>Référence</th><th>Type</th><th>Autorité</th><th>Émis le</th><th>État</th></tr></thead>
            <tbody>
              {s.titres.map((t) => (
                <tr key={t.id}>
                  <td><code>{t.reference}</code></td><td>{t.type_code}</td>
                  <td>{t.autorite_emettrice}</td>
                  <td>{new Date(t.date_emission).toLocaleDateString('fr-FR')}</td>
                  <td><span className={`etat ${t.statut === 'valide' ? 'vert' : 'rouge'}`}>{t.statut}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {!s.acces.titres_lisibles && (
        <p className="note-indispo">Les titres et actes relèvent du notariat — votre rôle ne les ouvre pas.</p>
      )}

      {s.litiges.length > 0 && (
        <>
          <h3 className="titre-section">Litiges</h3>
          <table className="tableau large">
            <thead><tr><th>Référence</th><th>Objet</th><th>Statut</th><th>Ouvert le</th></tr></thead>
            <tbody>
              {s.litiges.map((l) => (
                <tr key={l.reference}>
                  <td><code>{l.reference}</code></td><td>{l.objet}</td>
                  <td><span className={`etat ${l.statut === 'clos' ? 'gris' : 'rouge'}`}>{l.statut}</span></td>
                  <td>{new Date(l.ouvert_le).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ⚠️ L'HISTORIQUE EST RENDU AVEC L'ACTUEL, jamais dans un autre écran.
          Un notaire vient établir une CHAÎNE de titres : reléguée ailleurs,
          elle n'est jamais consultée. */}
      {s.droits.historique.length > 0 && (
        <details>
          <summary>Droits éteints <span className="aide">({s.droits.historique.length}) — la chaîne des titres</span></summary>
          <TableDroits droits={s.droits.historique} />
        </details>
      )}

      {s.mutations.length > 0 && (
        <details>
          <summary>Mutations sur ce fonds <span className="aide">({s.mutations.length})</span></summary>
          <table className="tableau large">
            <thead><tr><th>Référence</th><th>Type</th><th>Statut</th><th>Déposée</th><th>Appliquée</th></tr></thead>
            <tbody>
              {s.mutations.map((m) => (
                <tr key={m.reference}>
                  <td><code>{m.reference}</code></td><td>{m.type}</td>
                  <td>{m.statut}</td>
                  <td>{new Date(m.depose_le).toLocaleDateString('fr-FR')}</td>
                  {/* `applique_le` a menti pendant des mois : il se remplissait à
                      la validation alors que rien n'était appliqué. On le montre
                      pour ce qu'il est — la preuve que le registre a changé. */}
                  <td>{m.applique_le
                    ? new Date(m.applique_le).toLocaleDateString('fr-FR')
                    : <span className="aide">non appliquée</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

function TableDroits({ droits }: { droits: DroitAffiche[] }) {
  return (
    <table className="tableau large">
      <thead><tr>
        <th>Nature</th><th>Titulaire</th><th>Quotité</th><th>Rang</th>
        <th>Depuis</th><th>Jusqu'à</th><th>Mutation</th>
      </tr></thead>
      <tbody>
        {droits.map((d) => (
          <tr key={d.id}>
            <td><strong>{ETIQUETTE_NATURE[d.nature] ?? d.nature}</strong></td>
            <td>
              {'masque' in d.titulaire
                ? <span className="aide">nom masqué — sensibilité 2 requise</span>
                : (
                  <>
                    {d.titulaire.denomination}
                    <br />
                    <span className="aide">
                      {d.titulaire.type.replace(/_/g, ' ')}
                      {d.titulaire.identite_verifiee ? ' · identité vérifiée' : ' · identité non vérifiée'}
                      {d.titulaire.nin_rattache ? ' · NIN rattaché' : ''}
                    </span>
                  </>
                )}
            </td>
            {/* La fraction telle qu'inscrite. Jamais convertie en pourcentage :
                1/3 deviendrait 33,3 %, et trois tiers cesseraient de faire le tout. */}
            <td className="num">{d.quotite}</td>
            <td className="num">{d.rang ?? '—'}</td>
            <td>{new Date(d.valide_du).toLocaleDateString('fr-FR')}</td>
            <td>{d.valide_au ? new Date(d.valide_au).toLocaleDateString('fr-FR') : '—'}</td>
            <td>{d.mutation ? <code>{d.mutation}</code> : <span className="aide">hors mutation</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RechercheTitulaires(
  { onOuvrirParcelle, peutInscrire, titulaireInitial }:
  {
    onOuvrirParcelle: (id: string) => void; peutInscrire: boolean;
    /** La personne demandée depuis ailleurs — la recherche universelle. */
    titulaireInitial?: string | null;
  },
) {
  const [q, setQ] = useState('');
  const [inscription, setInscription] = useState(false);
  const [resultats, setResultats] = useState<TitulaireResume[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [fiche, setFiche] = useState<FicheTitulaire | null>(null);

  /**
   * ⚠️ ON SUIT LES DEMANDES SUIVANTES, pas seulement la première : l'état
   * initial d'un `useState` est figé au montage, et cet écran reste monté d'une
   * recherche à l'autre. Un second clic sur une AUTRE personne n'aurait rien
   * fait — le défaut « presque juste » que ce dépôt traque.
   */
  useEffect(() => {
    if (!titulaireInitial) return;
    let vivant = true;
    void api.foncier.titulaire(titulaireInitial)
      .then((f) => { if (vivant) setFiche(f); })
      .catch((e) => { if (vivant) setErreur(e instanceof ErreurApi ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [titulaireInitial]);

  const [erreur, setErreur] = useState<string | null>(null);

  async function chercher(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setFiche(null);
    try {
      const r = await api.foncier.chercherTitulaires(q.trim());
      setResultats(r.titulaires); setMessage(r.message ?? null);
    } catch (err) {
      setResultats(null);
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    }
  }

  return (
    <>
      <div className="avertissement">
        <strong>Chaque recherche est consignée au journal, y compris celles qui ne rendent rien.</strong>
        {' '}Consulter le patrimoine d'une personne est un acte, pas une lecture. Ne tracer que
        les recherches fructueuses laisserait balayer des noms sans laisser d'empreinte.
      </div>

      {/* ⚠️ ON NE POUVAIT ENTRER AU REGISTRE QU'EN ACHETANT. Une personne
          n'existait que si une mutation la faisait naître : inscrire quelqu'un
          qui POSSÈDE DÉJÀ — le cas ordinaire d'un recensement — n'avait aucune
          porte. C'est ce bouton. */}
      <div className="actions" style={{ marginBottom: 10 }}>
        <button className="bouton" disabled={!peutInscrire}
                title={peutInscrire
                  ? "Inscrire une personne qui détient déjà, sans passer par une mutation"
                  : "Votre rôle ne permet pas d'inscrire une personne (fon.titulaire:creer)"}
                onClick={() => setInscription((v) => !v)}>
          {inscription ? 'Fermer' : 'Inscrire une personne'}
        </button>
      </div>

      {inscription && <FormulaireInscription onFerme={() => setInscription(false)} />}

      <form className="recherche large" onSubmit={chercher}>
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Nom ou raison sociale — au moins 2 caractères"
          aria-label="Nom d'un titulaire" minLength={2}
        />
        <button className="bouton primaire" disabled={q.trim().length < 2}>Rechercher</button>
      </form>

      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {message && <p className="vide">{message}</p>}

      {resultats && resultats.length > 0 && (
        <table className="tableau large">
          <thead><tr><th>Dénomination</th><th>Type</th><th>Identité</th><th>Droits actifs</th><th /></tr></thead>
          <tbody>
            {resultats.map((t) => (
              <tr key={t.id}>
                <td><strong>{t.denomination}</strong></td>
                <td>{t.type.replace(/_/g, ' ')}</td>
                <td>
                  {t.identite_verifiee
                    ? <span className="etat vert">vérifiée</span>
                    : <span className="etat ambre">non vérifiée</span>}
                  {t.nin_rattache && <span className="aide"> · NIN</span>}
                </td>
                <td className="num">{t.droits_actifs}</td>
                <td className="actions-ligne">
                  <button className="bouton discret" onClick={async () => {
                    try { setFiche(await api.foncier.titulaire(t.id)); }
                    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); }
                  }}>Patrimoine</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {fiche && (
        <div className="fiche-formulaire">
          <h3>{fiche.titulaire.denomination}</h3>
          <p>{fiche.resume}</p>
          {fiche.patrimoine.actifs.length > 0 && (
            <table className="tableau large">
              <thead><tr><th>Parcelle</th><th>Division</th><th>Nature</th><th>Quotité</th><th>Surface</th></tr></thead>
              <tbody>
                {fiche.patrimoine.actifs.map((b, k) => (
                  <tr key={k}>
                    <td><button className="lien" onClick={() => onOuvrirParcelle(b.parcelle)}>
                      <code>{b.parcelle}</code></button></td>
                    <td>{b.division ?? '—'}</td>
                    <td>{ETIQUETTE_NATURE[b.nature] ?? b.nature}</td>
                    <td className="num">{b.quotite_num}/{b.quotite_den}</td>
                    <td className="num">{Number(b.surface_m2).toLocaleString('fr-FR')} m²</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="actions">
            <button className="bouton discret" onClick={() => setFiche(null)}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
}

function FormulaireRestriction(
  { parcelle, onFerme, onInscrite }:
  { parcelle: string; onFerme: () => void; onInscrite: (message: string) => void },
) {
  const [type, setType] = useState<string>('servitude_passage');
  const [description, setDescription] = useState('');
  const [beneficiaire, setBeneficiaire] = useState('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const servitude = type.startsWith('servitude');

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.foncier.inscrireRestriction({
        identifiantParcelle: parcelle, type, description: description.trim(),
        beneficiaireParcelle: beneficiaire.trim() || undefined, motif: motif.trim(),
      });
      onInscrite(r.message);
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      <h3>Grever <code>{parcelle}</code></h3>
      <p className="mention">
        Une restriction grève le fonds <strong>sans changer son titulaire</strong>. Elle
        n'appelle donc ni double validation ni radiation — contrairement à une mutation.
      </p>
      <label>Type
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES_RESTRICTION.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
      </label>
      <label>Description <span className="aide">ce qui est grevé, et dans quelle mesure</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  rows={3} minLength={10} required />
      </label>
      {servitude && (
        <label>Parcelle bénéficiaire <span className="aide">le fonds dominant</span>
          <input value={beneficiaire} onChange={(e) => setBeneficiaire(e.target.value)}
                 placeholder="GN-PAR-…" />
        </label>
      )}
      <label>Motif <span className="aide">consigné au journal</span>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} minLength={3} required />
      </label>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire"
                disabled={envoi || description.trim().length < 10 || motif.trim().length < 3}>
          {envoi ? 'Inscription…' : 'Inscrire la restriction'}
        </button>
      </div>
    </form>
  );
}

/**
 * INSCRIRE UNE PERSONNE — sans mutation, sans droit.
 *
 * ⚠️ INSCRIRE N'EST PAS ATTRIBUER, et le formulaire le dit. Aucun champ ne
 * porte de parcelle ni de quotité : un droit naît d'une mutation validée à
 * quatre yeux, jamais d'un écran d'état civil. Laisser croire l'inverse ferait
 * de ce formulaire la seconde porte d'écriture du foncier.
 */
function FormulaireInscription({ onFerme }: { onFerme: () => void }) {
  const [type, setType] = useState('personne_physique');
  const [denomination, setDenomination] = useState('');
  const [nin, setNin] = useState('');
  const [ninSource, setNinSource] = useState('carte_cedeao');
  const [mid, setMid] = useState('');
  const [motif, setMotif] = useState('');
  const [resultat, setResultat] = useState<ResultatInscription | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null); setResultat(null); setEnvoi(true);
    try {
      setResultat(await api.titulaires.inscrire({
        type, denomination: denomination.trim(), motif: motif.trim(),
        ...(nin.trim() ? { nin: nin.trim(), ninSource } : {}),
        ...(mid.trim() ? { mowiboMid: mid.trim() } : {}),
      }));
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : String(err));
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire large" onSubmit={soumettre}>
      <h4>Inscrire une personne au registre</h4>
      <p className="note-indispo">
        Cette inscription ne crée <strong>aucun droit</strong>. Elle enregistre une identité,
        pour qu'une instruction puisse ensuite être menée à son nom. La propriété s'inscrit
        par une mutation, validée à quatre yeux.
      </p>

      <label>Qualité
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="personne_physique">Personne physique</option>
          <option value="personne_morale">Personne morale</option>
          <option value="indivision">Indivision</option>
          <option value="communaute_coutumiere">Communauté coutumière</option>
          <option value="collectivite">Collectivité</option>
          <option value="etat">État</option>
        </select>
      </label>

      <label>Dénomination <span className="aide">nom complet, ou raison sociale</span>
        <input value={denomination} onChange={(e) => setDenomination(e.target.value)}
               required minLength={2} />
      </label>

      <label>Identifiant national <span className="aide">facultatif — sans lui, aucun rapprochement fiscal ne sera possible</span>
        <input value={nin} onChange={(e) => setNin(e.target.value)}
               placeholder="NIN (optionnel)" />
      </label>

      {nin.trim() && (
        <label>Source du numéro
          <select value={ninSource} onChange={(e) => setNinSource(e.target.value)}>
            {/* ⚠️ « Déclaré » N'EST PAS une vérification, et le service le
                traite comme tel : un numéro annoncé par l'intéressé reste non
                vérifié tant qu'une pièce ne l'a pas établi. */}
            <option value="carte_cedeao">Carte CEDEAO — vérifiée sur pièce</option>
            <option value="mowibo_id">Mowibo ID — vérifiée</option>
            <option value="declaration">Déclaré par l'intéressé — NON vérifié</option>
            <option value="import_fiscal">Import fiscal</option>
          </select>
        </label>
      )}

      <label>Adresse Mowibo Post <span className="aide">facultatif — le MID, jamais un DID ; sans lui aucun courrier ne peut partir</span>
        <input value={mid} onChange={(e) => setMid(e.target.value)} placeholder="MID (optionnel)" />
      </label>

      <label>Motif <span className="aide">consigné au journal, non modifiable ensuite</span>
        <textarea value={motif} onChange={(e) => setMotif(e.target.value)} rows={2} required minLength={3} />
      </label>

      {resultat && (
        <div className="succes" role="status">
          <p>{resultat.message}</p>
          <p className="discret">
            <code>{resultat.titulaire.id}</code> — identité{' '}
            {resultat.identiteVerifiee ? 'vérifiée sur pièce' : 'NON vérifiée'} ·{' '}
            {resultat.joignableParCourrier ? 'joignable par courrier' : 'non joignable par courrier'}
          </p>
          {resultat.homonymes.length > 0 && (
            <ul className="liste-fine">
              {resultat.homonymes.map((h) => (
                <li key={h.id}>Déjà inscrit sous ce nom : <code>{h.id}</code></li>
              ))}
            </ul>
          )}
        </div>
      )}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Fermer</button>
        <button className="bouton primaire"
                disabled={envoi || denomination.trim().length < 2 || motif.trim().length < 3}>
          {envoi ? 'Inscription…' : 'Inscrire'}
        </button>
      </div>
    </form>
  );
}
